// sovHelpers.js
// Pure functions used by SOVTab3 — no React, no state, fully testable in
// isolation. Nothing here reads or writes component state; everything is
// passed in as arguments and returned as plain values.

export const getAddressString = (address = {}) =>
  `${address.street || ""}, ${address.city || ""}, ${address.state || ""}, ${
    address.country || ""
  } ${address.postalCode || ""}`;

// Normalizes sendToEmail from any stored shape (array, "a,b" string,
// ["a,b"], null) into a clean array of individual emails.
export const toEmailArray = (value) =>
  (Array.isArray(value) ? value : [value])
    .flatMap((v) => String(v ?? "").split(","))
    .map((e) => e.trim())
    .filter(Boolean);

export const formatDollarValue = (num) =>
  Number.isFinite(parseFloat(num))
    ? `$${parseFloat(num).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "$0.00";

// Local-timezone YYYY-MM-DD (avoids the UTC off-by-one from toISOString)
export const localDateStr = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const addDaysToDateStr = (dateStr, days) => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return localDateStr(dt);
};

export const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "N/A";
  return dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
};

export const formatLocalDateNice = (isoOrDate) => {
  if (!isoOrDate) return "N/A";
  if (typeof isoOrDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)) {
    const [year, month, day] = isoOrDate.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  const d = new Date(isoOrDate);
  if (isNaN(d.getTime())) return "Invalid date";
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const renumberItems = (items) =>
  items.map((it, idx) => ({ ...it, itemNumber: idx + 1 }));

// Minimum allowed % for a row — you can never bill backwards.
export const minPercentForItem = (item) => {
  const sv = parseFloat(item.scheduledValue) || 0;
  const prev = parseFloat(item.workCompletedPreviously) || 0;
  if (sv === 0) return 0;
  const raw = (prev / sv) * 100;
  return Math.min(100, Math.max(0, raw));
};

export const statusPillClass = (status) => {
  switch (status) {
    case "paid":
      return "bg-green-600/20 text-green-300 border border-green-600/40";
    case "sent":
      return "bg-blue-600/20 text-blue-300 border border-blue-600/40";
    case "voided":
      return "bg-red-600/20 text-red-300 border border-red-600/40";
    case "generated":
    case "invoiced":
      return "bg-yellow-600/20 text-yellow-300 border border-yellow-600/40";
    default:
      return "bg-gray-600/40 text-gray-300 border border-gray-500/40";
  }
};

export const customSelectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#4b5563",
    borderColor: s.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: s.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "13px",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb", margin: 0, padding: 0 }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected ? "#4b5563" : "#374151",
    color: s.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "13px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    border: "1px solid #4b5563",
    zIndex: 50,
  }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb" }),
  placeholder: (p) => ({ ...p, color: "#9ca3af" }),
  multiValue: (p) => ({ ...p, backgroundColor: "#4b5563" }),
  multiValueLabel: (p) => ({ ...p, color: "#e5e7eb" }),
  multiValueRemove: (p) => ({
    ...p,
    color: "#e5e7eb",
    "&:hover": { backgroundColor: "#f97316", color: "#fff" },
  }),
};

const isIgnoredBreakdownItem = (description = "") =>
  /(CN|CQ|T&M)/i.test(description);

export const getBreakdownAllocation = (
  customBreakdownItems,
  originalContractValue,
) => {
  const contractValue = parseFloat(originalContractValue) || 0;
  let allocated = 0;
  let excludedCount = 0;
  customBreakdownItems.forEach((item) => {
    if (isIgnoredBreakdownItem(item.description || "")) {
      excludedCount++;
      return;
    }
    allocated += parseFloat(item.amount) || 0;
  });
  const remaining = contractValue - allocated;
  return {
    contractValue,
    allocated,
    remaining,
    excludedCount,
    isBalanced: Math.abs(remaining) < 0.005,
    isOver: remaining < -0.005,
  };
};

export const validateBreakdownTotal = (
  customBreakdownItems,
  originalContractValue,
) => {
  const { allocated, contractValue, remaining, isBalanced } =
    getBreakdownAllocation(customBreakdownItems, originalContractValue);
  if (!isBalanced) {
    return (
      `Breakdown total (${formatDollarValue(allocated)}) must equal the Original Contract Value ` +
      `(${formatDollarValue(contractValue)}). ` +
      (remaining > 0
        ? `Add ${formatDollarValue(remaining)} more.`
        : `Reduce by ${formatDollarValue(Math.abs(remaining))}.`)
    );
  }
  return null;
};

// ── PREVIOUS-VALUE COMPUTATION (positional match) ───────────────────────
export const computePreviousValues = (
  targetItems,
  allSovs,
  currentSovId,
  currentDrawNumber,
  currentInvoiceDate = null,
) => {
  const toTime = (d) => {
    if (!d) return -Infinity;
    const t = new Date(d).getTime();
    return isNaN(t) ? -Infinity : t;
  };
  const currentTime = toTime(currentInvoiceDate);

  const previousSov = allSovs
    .filter((s) => {
      if (s._id === currentSovId) return false;
      const sDraw = s.drawNumber || 0;
      if (sDraw < currentDrawNumber) return true;
      if (
        sDraw === currentDrawNumber &&
        currentTime !== -Infinity &&
        toTime(s.invoiceDate) < currentTime
      ) {
        return true;
      }
      return false;
    })
    .sort((a, b) => {
      const dateDiff = toTime(b.invoiceDate) - toTime(a.invoiceDate);
      if (dateDiff !== 0) return dateDiff;
      return (b.drawNumber || 0) - (a.drawNumber || 0);
    })[0];

  if (!previousSov) {
    return targetItems.map((item) => ({
      ...item,
      workCompletedPreviously: "0.00",
    }));
  }

  const prevOriginalItems = previousSov.items.filter(
    (i) => !i.changeOrderNumber,
  );
  const prevCoItems = previousSov.items.filter((i) => !!i.changeOrderNumber);
  let originalIndex = 0;

  return targetItems.map((item) => {
    let prevItem = null;
    if (!item.changeOrderNumber) {
      prevItem = prevOriginalItems[originalIndex] || null;
      originalIndex++;
    } else {
      prevItem = prevCoItems.find(
        (pi) => String(pi.changeOrderNumber) === String(item.changeOrderNumber),
      );
    }

    const prevCompleted = parseFloat(prevItem?.totalCompletedToDate || 0);
    const workThisPeriod = parseFloat(item.workCompletedThisPeriod || 0);
    const totalCompleted = prevCompleted + workThisPeriod;
    const scheduledValue = parseFloat(item.scheduledValue || 0);

    return {
      ...item,
      workCompletedPreviously: prevCompleted.toFixed(2),
      totalCompletedToDate: totalCompleted.toFixed(2),
      percentage: scheduledValue
        ? ((totalCompleted / Math.abs(scheduledValue)) * 100).toFixed(2)
        : "0.00",
      balanceToFinish: (scheduledValue - totalCompleted).toFixed(2),
    };
  });
};

// ── CO RECONCILIATION (v3-specific) ─────────────────────────────────────
export const reconcileItemsWithCOs = (items, allCOs) => {
  return items.map((item) => {
    if (!item.changeOrderNumber) return item;

    const co = allCOs.find(
      (c) => String(c.changeOrderNumber) === String(item.changeOrderNumber),
    );
    if (!co) return item;

    if (co.status === "Rejected" || co.status === "Reverted") {
      return {
        ...item,
        rejectedOrReverted: true,
        _coStatus: co.status,
        workCompletedThisPeriod: "0.00",
      };
    }

    if (co.status === "Approved") {
      const livePrice = parseFloat(co.totalCustomerPrice || 0);
      const currentSV = parseFloat(item.scheduledValue || 0);
      if (
        Math.abs(livePrice - currentSV) > 0.005 &&
        parseFloat(item.workCompletedThisPeriod || 0) === 0
      ) {
        const prevCompleted = parseFloat(item.workCompletedPreviously || 0);
        return {
          ...item,
          scheduledValue: livePrice.toFixed(2),
          balanceToFinish: (livePrice - prevCompleted).toFixed(2),
          percentage: livePrice
            ? ((prevCompleted / livePrice) * 100).toFixed(2)
            : "0.00",
          referenceNumber: item.referenceNumber || co.referenceNumber || null,
          _priceUpdated: true,
        };
      }
      if (!item.referenceNumber && co.referenceNumber) {
        return { ...item, referenceNumber: co.referenceNumber };
      }
    }

    return item;
  });
};

// Final integrity guard applied at save time: no item may end up billed
// below what previous draws already invoiced. Sign-aware — a negative
// scheduledValue (credit CO) must never bill back toward zero past what
// was already invoiced.
export const clampItemsForIntegrity = (items) => {
  let anyClamped = false;
  const clamped = items.map((item) => {
    const sv = parseFloat(item.scheduledValue) || 0;
    const prev = parseFloat(item.workCompletedPreviously) || 0;
    const thisPeriod = parseFloat(item.workCompletedThisPeriod) || 0;
    if (sv === 0 || prev === 0) return item;

    const wentBackwards = sv > 0 ? thisPeriod < -0.005 : thisPeriod > 0.005;
    if (!wentBackwards) return item;

    anyClamped = true;
    return {
      ...item,
      workCompletedThisPeriod: "0.00",
      totalCompletedToDate: prev.toFixed(2),
      percentage: sv ? ((prev / sv) * 100).toFixed(2) : "0.00",
      balanceToFinish: (sv - prev).toFixed(2),
    };
  });
  return { clamped, anyClamped };
};

// ── CREDIT MEMO SUPPORT ─────────────────────────────────────────────────
// A change-order row with a negative scheduledValue is billed as a QuickBooks
// Credit Memo (the whole CO amount), never netted into an invoice.
export const isCreditMemoItem = (item) =>
  !!item?.changeOrderNumber && (parseFloat(item.scheduledValue) || 0) < 0;

// Human label for a stored invoiceComponent.
export const invoiceComponentLabel = (ic) => {
  if (!ic) return "";
  const isCredit = ic.qbDocType === "credit_memo";
  if (ic.component === "holdback") return "Holdback Release";
  if (ic.component === "original_contract") return "Original Contract";
  if (ic.component === "change_order") {
    const n = ic.changeOrderNumber;
    const ref = ic.componentKey?.startsWith("ref_");
    const base = ref ? `Ref #${n}` : `CO #${n}`;
    return isCredit ? `Credit Memo – ${base}` : base;
  }
  return "Invoice";
};

// Invoice total for the draw. Credit-memo rows are excluded: they live on
// their own QB document and never reduce the invoice.
export const calculateTotalInvoiced = (
  items,
  withHoldback,
  quickBooksTaxRates,
  isQBConnected,
) => {
  const billable = items.filter((item) => !isCreditMemoItem(item));
  const thisPeriodSubtotal = billable.reduce(
    (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
    0,
  );
  const hst = billable.reduce((sum, item) => {
    const taxRate = isQBConnected
      ? quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)?.taxRates[0]
          ?.rate || 0
      : parseFloat(item.taxRate) || 0;
    return (
      sum + (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
    );
  }, 0);
  const subTotalWithTax = thisPeriodSubtotal + hst;
  const totalHoldback = withHoldback ? subTotalWithTax * 0.1 : 0;
  return (subTotalWithTax - totalHoldback).toFixed(2);
};
